import React, { useState, useEffect, Suspense, lazy } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight, Sparkles, Compass, Target, MessageCircle, Shield,
  BookOpen, Check, X, Loader2, Lock, Globe2, ChevronRight,
  Swords, Landmark, Anchor, Castle, Waves, Telescope, Radio, Crosshair
} from "lucide-react";
import { XR as api, xrSession } from "../lib/xr";
import GuidePanel from "../components/xr/GuidePanel";

const Scene3D = lazy(() => import("../components/xr/Scene3D"));

/*
  Stages:
   discover → explore → character → arjuna_profile → training → decision
   → learning → assessment → journey → worlds
*/

const WORLD_ICON = { Swords, BookOpen, Landmark, Anchor, Castle, Waves, Telescope, Sparkles };

export default function XR() {
  const [stage, setStage] = useState("discover");
  const [world, setWorld] = useState(null);
  const [guideOpen, setGuideOpen] = useState(false);
  const [chosenDecision, setChosenDecision] = useState(null);
  const [assessment, setAssessment] = useState({ answers: [], submitted: null });
  const session = xrSession();

  useEffect(() => {
    api.world("kurukshetra").then(setWorld).catch(() => {});
    api.event("experience_started", {}, session);
  }, [session]);

  const go = (next, event, data) => {
    if (event) api.event(event, data || {}, session);
    setStage(next);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="mythos-ink-bg min-h-screen" data-testid="xr-page">
      {/* Floating guide toggle (only inside experience, not discover) */}
      {stage !== "discover" && (
        <button
          onClick={() => setGuideOpen(true)}
          className="fixed bottom-6 right-6 z-30 mythos-gold-bg text-[#0E0F12] rounded-full px-5 py-3 font-bold shadow-2xl inline-flex items-center gap-2 hover:opacity-90 transition"
          data-testid="xr-guide-open"
        >
          <MessageCircle size={16} /> Ask the Guide
        </button>
      )}
      {guideOpen && <GuidePanel onClose={() => setGuideOpen(false)} />}

      <AnimatePresence mode="wait">
        {stage === "discover" && <Discover key="d" onEnter={() => go("explore", "world_entered", { world: "kurukshetra" })} />}
        {stage === "explore" && <Explore key="e" world={world} onNext={() => go("character")} />}
        {stage === "character" && <CharacterSelect key="c" world={world} onPick={(slug) => go(slug === "arjuna" ? "arjuna_profile" : "character", "character_selected", { character: slug })} />}
        {stage === "arjuna_profile" && <ArjunaProfile key="a" world={world} onBegin={() => go("training", "training_started", {})} />}
        {stage === "training" && <Training key="t" onDone={() => go("decision", "training_completed", {})} />}
        {stage === "decision" && <Decision key="dec" world={world} chosen={chosenDecision} onChoose={(choice) => { setChosenDecision(choice); api.event("decision_selected", { choice: choice.letter }, session); }} onNext={() => go("learning")} />}
        {stage === "learning" && <LearningMoment key="l" world={world} onNext={() => go("assessment", "knowledge_test_started", {})} />}
        {stage === "assessment" && <Assessment key="q" world={world} session={session} answers={assessment.answers} setAnswers={(a) => setAssessment((s) => ({ ...s, answers: a }))} submitted={assessment.submitted} setSubmitted={(sr) => setAssessment((s) => ({ ...s, submitted: sr }))} onDone={() => go("journey", "knowledge_test_completed", { score: assessment.submitted?.score })} />}
        {stage === "journey" && <Journey key="j" submitted={assessment.submitted} onNext={() => go("worlds", "experience_completed", {})} />}
        {stage === "worlds" && <WorldsShowcase key="w" />}
      </AnimatePresence>
    </div>
  );
}

// =========================== DISCOVER (Landing) ===========================
function Discover({ onEnter }) {
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="relative overflow-hidden">
      {/* 3D hero background */}
      <div className="absolute inset-0 opacity-55" aria-hidden>
        <Suspense fallback={<div className="w-full h-full mythos-ink-bg" />}>
          <Scene3D />
        </Suspense>
      </div>
      <div className="absolute inset-0 bg-gradient-to-b from-[#0E0F12]/60 via-[#0E0F12]/40 to-[#0E0F12]" aria-hidden />

      <div className="relative max-w-7xl mx-auto px-5 md:px-10 pt-24 md:pt-32 pb-32">
        <div className="flex items-center gap-2 text-[11px] tracking-[0.28em] uppercase mythos-gold font-bold mb-6">
          <Sparkles size={14} /> EduSphere XR · Vertical slice prototype
        </div>
        <h1 className="font-mythos text-5xl md:text-7xl lg:text-8xl font-extrabold tracking-tight leading-[1.02] text-[#F4EFE6] max-w-4xl">
          <span className="mythos-gold">KURUKSHETRA</span>
        </h1>
        <p className="font-mythos text-xl md:text-2xl text-[#F4EFE6]/85 mt-5 max-w-2xl">
          Enter the world of the Mahabharata.
        </p>
        <p className="text-[#E8EBF0]/70 mt-3 max-w-2xl leading-relaxed">
          An interactive learning experience where story, strategy, culture and knowledge meet.
        </p>

        <div className="mt-10 flex flex-wrap gap-3">
          <button onClick={onEnter} className="mythos-gold-bg text-[#0E0F12] rounded-full px-7 py-3.5 font-bold inline-flex items-center gap-2 hover:opacity-90 transition" data-testid="xr-enter-experience">
            ENTER THE EXPERIENCE <ArrowRight size={16} />
          </button>
          <button onClick={onEnter} className="border border-[#C9A66B] text-[#C9A66B] rounded-full px-7 py-3.5 font-bold inline-flex items-center gap-2 hover:bg-[#C9A66B] hover:text-[#0E0F12] transition" data-testid="xr-explore">
            EXPLORE THE WORLD
          </button>
          <div className="inline-flex items-center gap-2 text-xs text-[#F4EFE6]/60 border border-[#2A3349] rounded-full px-4 py-2" data-testid="xr-preview-badge" title="WebXR not yet implemented. Architecture is XR-ready.">
            <Radio size={12} /> XR PREVIEW · headset support in roadmap
          </div>
        </div>

        <div className="text-xs text-[#F4EFE6]/50 mt-3">Designed for desktop, mobile and immersive XR experiences.</div>

        {/* WHY band */}
        <div className="grid md:grid-cols-2 gap-4 mt-20 max-w-5xl" data-testid="xr-why">
          <div className="vault-card rounded-2xl p-7 border border-[#2A3349]">
            <div className="text-[11px] tracking-[0.24em] uppercase mythos-gold font-bold mb-3">Why EduSphere XR?</div>
            <p className="font-mythos text-lg text-[#F4EFE6] leading-relaxed">
              India has thousands of stories, historical sites, scientific traditions and cultural experiences that can be transformed into interactive learning worlds.
            </p>
            <p className="mt-4 font-mythos text-base mythos-gold font-bold">
              ONE WORLD IS A DEMONSTRATION.<br/>THE PLATFORM IS THE OPPORTUNITY.
            </p>
          </div>
          <div className="vault-card rounded-2xl p-7 border border-[#2A3349]">
            <div className="text-[11px] tracking-[0.24em] uppercase mythos-gold font-bold mb-3">Knowledge Note</div>
            <p className="text-sm text-[#E8EBF0]/75 leading-relaxed">
              This experience is an educational digital interpretation inspired by the Mahabharata tradition.
              Character details, locations and events are presented through a combination of traditional narratives,
              scholarly interpretation and creative reconstruction.
            </p>
            <div className="mt-5 flex flex-wrap gap-2">
              {["Epic tradition", "Cultural tradition", "Scholarly interpretation"].map((t) => (
                <span key={t} className="text-[10px] tracking-[0.14em] uppercase mythos-gold border border-[#3A3D44] rounded-full px-2.5 py-1 font-bold">{t}</span>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-12 flex flex-wrap items-center gap-4 text-xs">
          <Link to="/xr/partners" className="text-[#C9A66B] font-semibold hover:underline inline-flex items-center gap-1" data-testid="xr-partners-link">
            For partners &amp; investors <ArrowRight size={12} />
          </Link>
          <Link to="/" className="text-[#F4EFE6]/55 hover:text-[#F4EFE6]">← Back to EduSphere</Link>
        </div>
      </div>
    </motion.div>
  );
}

// =========================== EXPLORE (3D Observer) ===========================
function Explore({ world, onNext }) {
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="relative" data-testid="xr-explore">
      <div className="relative w-full" style={{ height: "70vh", minHeight: 500 }}>
        <Suspense fallback={<FullBleedLoading />}>
          <Scene3D />
        </Suspense>
        <div className="absolute top-5 left-5 right-5 flex items-start justify-between pointer-events-none">
          <div className="pointer-events-auto">
            <div className="text-[11px] tracking-[0.22em] uppercase mythos-gold font-bold">Observer Mode</div>
            <div className="font-mythos text-2xl md:text-3xl font-extrabold text-[#F4EFE6] mt-1 drop-shadow-lg">The plain of Kurukshetra</div>
            <div className="text-xs text-[#F4EFE6]/75 mt-1 max-w-md drop-shadow">Rotate to look around. Two camps stand at either edge of the plain. The field is set.</div>
          </div>
          <div className="pointer-events-auto text-[10px] tracking-[0.18em] uppercase mythos-gold border border-[#C9A66B]/40 rounded-full px-3 py-1.5 font-bold bg-[#0E0F12]/60 backdrop-blur">
            Drag to rotate · Scroll to zoom
          </div>
        </div>
      </div>
      <div className="max-w-6xl mx-auto px-5 md:px-10 py-10">
        <div className="grid sm:grid-cols-3 gap-4 mb-8" data-testid="xr-explore-markers">
          {[
            { icon: Shield, title: "Pandava side", sub: "Five brothers, led traditionally by Yudhishthira" },
            { icon: Swords, title: "Kaurava side", sub: "Hundred cousins, led by Duryodhana" },
            { icon: Compass, title: "The open plain", sub: "Clear sightlines — the formations will be visible" },
          ].map((m, i) => (
            <div key={i} className="vault-card rounded-xl p-5 border border-[#2A3349]">
              <m.icon className="mythos-gold mb-3" size={20} />
              <div className="font-mythos font-bold text-[#F4EFE6]">{m.title}</div>
              <div className="text-xs text-[#E8EBF0]/60 mt-1">{m.sub}</div>
            </div>
          ))}
        </div>
        <button onClick={onNext} className="mythos-gold-bg text-[#0E0F12] rounded-full px-7 py-3.5 font-bold inline-flex items-center gap-2 hover:opacity-90 transition" data-testid="xr-explore-next">
          Choose your perspective <ArrowRight size={16} />
        </button>
      </div>
    </motion.div>
  );
}

// =========================== CHARACTER SELECT ===========================
function CharacterSelect({ world, onPick }) {
  if (!world) return <FullBleedLoading />;
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="max-w-7xl mx-auto px-5 md:px-10 py-16" data-testid="xr-character-select">
      <div className="text-[11px] tracking-[0.24em] uppercase mythos-gold font-bold mb-3">Stage 02 · Perspective</div>
      <h2 className="font-mythos text-4xl md:text-5xl font-extrabold tracking-tight text-[#F4EFE6]">Choose your perspective.</h2>
      <p className="text-[#E8EBF0]/65 mt-3 max-w-2xl">Each perspective frames the same battlefield differently. Arjuna is available in this vertical slice. The others arrive with future worlds.</p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-10">
        {world.characters.map((c, i) => {
          const available = c.status === "available";
          return (
            <motion.button
              key={c.slug}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.07 }}
              onClick={() => available && onPick(c.slug)}
              disabled={!available}
              data-testid={`xr-character-${c.slug}`}
              className={`text-left vault-card rounded-2xl p-6 border transition ${
                available ? "border-[#C9A66B] hover:scale-[1.02]" : "border-[#2A3349] opacity-60 cursor-not-allowed"
              }`}
            >
              <div className="flex items-center justify-between mb-4">
                <div className="w-14 h-14 rounded-full mythos-gold-bg text-[#0E0F12] grid place-items-center font-mythos font-extrabold text-lg">
                  {c.name[0]}
                </div>
                {available ? (
                  <span className="text-[10px] tracking-[0.18em] uppercase mythos-gold font-bold">Available</span>
                ) : (
                  <Lock size={14} className="text-[#7d8294]" />
                )}
              </div>
              <div className="font-mythos font-extrabold text-xl text-[#F4EFE6]">{c.name}</div>
              <div className="text-xs mythos-gold font-bold mt-1">{c.role}</div>
              <div className="text-xs text-[#E8EBF0]/60 mt-3">{c.context}</div>
              {available && <div className="mt-5 inline-flex items-center gap-1 mythos-gold font-bold text-sm">Begin as Arjuna <ArrowRight size={14} /></div>}
            </motion.button>
          );
        })}
      </div>
    </motion.div>
  );
}

// =========================== ARJUNA PROFILE ===========================
function ArjunaProfile({ world, onBegin }) {
  const arjuna = world?.characters?.find((c) => c.slug === "arjuna");
  if (!arjuna) return <FullBleedLoading />;
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="max-w-5xl mx-auto px-5 md:px-10 py-16" data-testid="xr-arjuna-profile">
      <div className="text-[11px] tracking-[0.24em] uppercase mythos-gold font-bold mb-3">Stage 03 · Character</div>
      <h2 className="font-mythos text-4xl md:text-5xl font-extrabold tracking-tight text-[#F4EFE6]">ARJUNA</h2>
      <div className="text-sm mythos-gold font-bold mt-1">{arjuna.role}</div>
      <div className="grid md:grid-cols-2 gap-6 mt-8">
        <div className="vault-card rounded-2xl p-6 border border-[#2A3349]">
          <div className="text-[11px] tracking-[0.18em] uppercase mythos-gold font-bold mb-2">Relationships</div>
          <div className="text-sm text-[#E8EBF0]/80 leading-relaxed">{arjuna.relationships}</div>
        </div>
        <div className="vault-card rounded-2xl p-6 border border-[#2A3349]">
          <div className="text-[11px] tracking-[0.18em] uppercase mythos-gold font-bold mb-2">Known for</div>
          <ul className="space-y-2 text-sm text-[#E8EBF0]/80">
            {arjuna.skills?.map((s, i) => (
              <li key={i} className="flex gap-2"><Check size={14} className="mythos-gold mt-0.5 flex-shrink-0" />{s}</li>
            ))}
          </ul>
        </div>
      </div>
      <div className="vault-card rounded-2xl p-6 border border-[#2A3349] mt-6">
        <div className="text-[11px] tracking-[0.18em] uppercase mythos-gold font-bold mb-2">Context</div>
        <div className="text-sm text-[#E8EBF0]/80 leading-relaxed">{arjuna.context}</div>
      </div>
      <button onClick={onBegin} className="mt-10 mythos-gold-bg text-[#0E0F12] rounded-full px-7 py-3.5 font-bold inline-flex items-center gap-2 hover:opacity-90 transition" data-testid="xr-begin-training">
        BEGIN TRAINING <ArrowRight size={16} />
      </button>
    </motion.div>
  );
}

// =========================== TRAINING ===========================
function Training({ onDone }) {
  const [step, setStep] = useState(0);
  const [shots, setShots] = useState([]); // {hit:bool, x:%, y:%}
  const [targetKey, setTargetKey] = useState(0);
  const [targetPos, setTargetPos] = useState({ x: 50, y: 45 });
  const [shotCount, setShotCount] = useState(0);
  const [formation, setFormation] = useState(null);

  const shoot = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const cx = ((e.clientX - rect.left) / rect.width) * 100;
    const cy = ((e.clientY - rect.top) / rect.height) * 100;
    const dx = cx - targetPos.x;
    const dy = cy - targetPos.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    const hit = dist < 10;
    setShots((s) => [...s, { hit, x: cx, y: cy }]);
    const next = shotCount + 1;
    setShotCount(next);
    if (next >= 3) {
      // move to chariot phase after 3 shots
      setTimeout(() => setStep(1), 900);
    } else {
      setTimeout(() => {
        setTargetKey((k) => k + 1);
        setTargetPos({ x: 20 + Math.random() * 60, y: 25 + Math.random() * 40 });
      }, 600);
    }
  };

  if (step === 0) {
    const hits = shots.filter((s) => s.hit).length;
    return (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="max-w-5xl mx-auto px-5 md:px-10 py-14" data-testid="xr-training-archery">
        <div className="text-[11px] tracking-[0.24em] uppercase mythos-gold font-bold mb-3">Stage 04 · Training · 1 of 3</div>
        <h2 className="font-mythos text-3xl md:text-4xl font-extrabold tracking-tight text-[#F4EFE6] flex items-center gap-3"><Target className="mythos-gold" />Archery</h2>
        <p className="text-[#E8EBF0]/65 mt-3 max-w-xl">Click the target. Three attempts. The point is not to hit — it is to understand what trained attention means on a battlefield.</p>

        <div
          className="relative mt-8 vault-card rounded-2xl border border-[#2A3349] overflow-hidden cursor-crosshair"
          style={{ aspectRatio: "16/9", background: "radial-gradient(ellipse at center, #4a2c17 0%, #1a0f08 80%)" }}
          onClick={shoot}
          data-testid="xr-archery-field"
        >
          <div
            key={targetKey}
            className="absolute transition-all"
            style={{ left: `${targetPos.x}%`, top: `${targetPos.y}%`, transform: "translate(-50%,-50%)" }}
          >
            <motion.div initial={{ scale: 0, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: "spring", stiffness: 200 }}>
              <Crosshair className="mythos-gold" size={56} strokeWidth={1.5} />
            </motion.div>
          </div>
          {shots.map((s, i) => (
            <div key={i} className="absolute w-3 h-3 rounded-full pointer-events-none" style={{ left: `${s.x}%`, top: `${s.y}%`, transform: "translate(-50%,-50%)", background: s.hit ? "#C9A66B" : "#F4EFE6" }} />
          ))}
          <div className="absolute bottom-4 left-4 text-xs mythos-gold font-bold tracking-[0.18em] uppercase">Shots: {shotCount}/3 · Hits: {hits}</div>
        </div>
        <div className="mt-6 text-xs text-[#E8EBF0]/60 italic">Observation: in the epic, Arjuna is depicted as focused on the eye of the bird — not the branch, not the leaves. Focus is a skill before it is an outcome.</div>
      </motion.div>
    );
  }

  if (step === 1) {
    return (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="max-w-5xl mx-auto px-5 md:px-10 py-14" data-testid="xr-training-chariot">
        <div className="text-[11px] tracking-[0.24em] uppercase mythos-gold font-bold mb-3">Stage 04 · Training · 2 of 3</div>
        <h2 className="font-mythos text-3xl md:text-4xl font-extrabold tracking-tight text-[#F4EFE6]">Chariot &amp; terrain</h2>
        <p className="text-[#E8EBF0]/65 mt-3 max-w-xl">A chariot is not a weapon. It is a position machine — a way to be in the right place with your archer at the right time.</p>
        <div className="relative mt-8 w-full" style={{ height: 420 }}>
          <Suspense fallback={<FullBleedLoading />}><Scene3D /></Suspense>
        </div>
        <div className="grid sm:grid-cols-3 gap-4 mt-6">
          {[
            { title: "Movement", d: "Chariots pick the ground. Fast on flat, useless on broken terrain." },
            { title: "Positioning", d: "The commander chooses where the fight will be, not just whether it happens." },
            { title: "Visibility", d: "Elevation and formation decide what each side can and cannot see." },
          ].map((f, i) => (
            <div key={i} className="vault-card rounded-xl p-4 border border-[#2A3349]">
              <div className="text-[11px] tracking-[0.18em] uppercase mythos-gold font-bold mb-1">{f.title}</div>
              <div className="text-xs text-[#E8EBF0]/70">{f.d}</div>
            </div>
          ))}
        </div>
        <button onClick={() => setStep(2)} className="mt-8 mythos-gold-bg text-[#0E0F12] rounded-full px-6 py-3 font-bold inline-flex items-center gap-2 hover:opacity-90 transition" data-testid="xr-training-chariot-next">
          Study the formations <ArrowRight size={16} />
        </button>
      </motion.div>
    );
  }

  // step 2 — formation identification
  const markers = [
    { id: "pandava", label: "Pandava side", correct: "friend", x: 20, y: 60 },
    { id: "kaurava", label: "Kaurava side", correct: "foe", x: 80, y: 40 },
    { id: "centre", label: "Central spine", correct: "formation", x: 50, y: 50 },
  ];
  const complete = Object.keys(formation || {}).length === markers.length;

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="max-w-5xl mx-auto px-5 md:px-10 py-14" data-testid="xr-training-formation">
      <div className="text-[11px] tracking-[0.24em] uppercase mythos-gold font-bold mb-3">Stage 04 · Training · 3 of 3</div>
      <h2 className="font-mythos text-3xl md:text-4xl font-extrabold tracking-tight text-[#F4EFE6]">Formation identification</h2>
      <p className="text-[#E8EBF0]/65 mt-3 max-w-xl">Tap each marker and tag it. Learn to see the field like a strategist.</p>

      <div className="relative mt-8 vault-card rounded-2xl border border-[#2A3349] overflow-hidden" style={{ aspectRatio: "16/9", background: "linear-gradient(180deg,#2a1810 0%,#8c5a30 100%)" }}>
        {markers.map((m) => {
          const tagged = formation?.[m.id];
          return (
            <div key={m.id} className="absolute" style={{ left: `${m.x}%`, top: `${m.y}%`, transform: "translate(-50%,-50%)" }}>
              <div className={`w-6 h-6 rounded-full border-2 ${tagged ? "mythos-gold-bg border-[#C9A66B]" : "border-[#C9A66B] bg-[#0E0F12]/60"} grid place-items-center text-[10px] font-bold text-[#0E0F12]`}>
                {tagged ? "✓" : "?"}
              </div>
              <div className="absolute left-1/2 -translate-x-1/2 top-full mt-1 whitespace-nowrap text-[10px] tracking-[0.14em] uppercase font-bold text-[#F4EFE6]">
                {m.label} {tagged && <span className="mythos-gold">· {tagged}</span>}
              </div>
              {!tagged && (
                <div className="absolute left-1/2 -translate-x-1/2 top-full mt-6 flex gap-1 bg-[#0E0F12] border border-[#2A3349] rounded-full p-1">
                  {["friend", "foe", "formation"].map((t) => (
                    <button key={t} className="text-[9px] tracking-[0.14em] uppercase font-bold text-[#F4EFE6] hover:mythos-gold-bg hover:text-[#0E0F12] rounded-full px-2 py-1 transition"
                      onClick={() => setFormation({ ...(formation || {}), [m.id]: t })}>
                      {t}
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <button
        onClick={onDone}
        disabled={!complete}
        className="mt-10 mythos-gold-bg text-[#0E0F12] rounded-full px-7 py-3.5 font-bold inline-flex items-center gap-2 hover:opacity-90 transition disabled:opacity-50"
        data-testid="xr-training-done"
      >
        To the decision <ArrowRight size={16} />
      </button>
    </motion.div>
  );
}

// =========================== DECISION ===========================
function Decision({ world, chosen, onChoose, onNext }) {
  if (!world) return <FullBleedLoading />;
  const d = world.decision;
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="max-w-6xl mx-auto px-5 md:px-10 py-16" data-testid="xr-decision">
      <div className="text-[11px] tracking-[0.24em] uppercase mythos-gold font-bold mb-3">Stage 05 · The decision</div>
      <h2 className="font-mythos text-4xl md:text-5xl font-extrabold tracking-tight text-[#F4EFE6]">{d.title}</h2>
      <p className="text-[#E8EBF0]/75 mt-4 max-w-2xl leading-relaxed">{d.scenario}</p>

      <div className="grid md:grid-cols-3 gap-4 mt-10">
        {d.choices.map((c, i) => {
          const picked = chosen?.letter === c.letter;
          const dim = chosen && !picked;
          return (
            <motion.button
              key={c.letter}
              onClick={() => !chosen && onChoose(c)}
              disabled={!!chosen}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: dim ? 0.4 : 1, y: 0 }}
              transition={{ delay: i * 0.08 }}
              data-testid={`xr-decision-${c.letter}`}
              className={`text-left vault-card rounded-2xl p-6 border transition relative ${
                picked ? "border-[#C9A66B]" : chosen ? "border-[#2A3349]" : "border-[#2A3349] hover:border-[#C9A66B]"
              }`}
            >
              <div className="mythos-gold font-mythos font-extrabold text-5xl mb-3">{c.letter}</div>
              <div className="font-mythos font-bold text-xl text-[#F4EFE6]">{c.title}</div>
              {picked && (
                <div className="absolute top-5 right-5 w-7 h-7 rounded-full mythos-gold-bg text-[#0E0F12] grid place-items-center">
                  <Check size={14} />
                </div>
              )}
            </motion.button>
          );
        })}
      </div>

      {chosen && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mt-10 vault-card rounded-2xl p-7 border border-[#C9A66B]" data-testid="xr-decision-consequence">
          <div className="text-[11px] tracking-[0.22em] uppercase mythos-gold font-bold mb-2">Consequence</div>
          <p className="text-[#F4EFE6]/90 leading-relaxed">{chosen.consequence}</p>
          <div className="mt-4 pt-4 border-t border-[#2A3349]">
            <div className="text-[11px] tracking-[0.22em] uppercase mythos-gold font-bold mb-2">Insight</div>
            <p className="text-sm text-[#E8EBF0]/80 leading-relaxed">{chosen.insight}</p>
          </div>
          <div className="mt-5 text-xs text-[#E8EBF0]/55 italic">{d.note}</div>
          <button onClick={onNext} className="mt-6 mythos-gold-bg text-[#0E0F12] rounded-full px-6 py-3 font-bold inline-flex items-center gap-2 hover:opacity-90" data-testid="xr-decision-next">
            What did you discover? <ArrowRight size={16} />
          </button>
        </motion.div>
      )}
    </motion.div>
  );
}

// =========================== LEARNING MOMENT ===========================
function LearningMoment({ world, onNext }) {
  if (!world) return <FullBleedLoading />;
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="max-w-4xl mx-auto px-5 md:px-10 py-20" data-testid="xr-learning">
      <div className="text-[11px] tracking-[0.24em] uppercase mythos-gold font-bold mb-3">Stage 06 · Reflect</div>
      <h2 className="font-mythos text-4xl md:text-5xl font-extrabold tracking-tight text-[#F4EFE6]">WHAT DID YOU DISCOVER?</h2>
      <ul className="mt-10 space-y-5">
        {world.learning_points.map((p, i) => (
          <motion.li
            key={i}
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: i * 0.1 }}
            className="flex gap-4 items-start"
          >
            <div className="mythos-gold font-mythos font-extrabold text-3xl leading-none">0{i + 1}</div>
            <p className="text-[#F4EFE6]/85 leading-relaxed pt-1">{p}</p>
          </motion.li>
        ))}
      </ul>
      <button onClick={onNext} className="mt-12 mythos-gold-bg text-[#0E0F12] rounded-full px-7 py-3.5 font-bold inline-flex items-center gap-2 hover:opacity-90 transition" data-testid="xr-learning-next">
        Knowledge challenge <ArrowRight size={16} />
      </button>
    </motion.div>
  );
}

// =========================== ASSESSMENT ===========================
function Assessment({ world, session, answers, setAnswers, submitted, setSubmitted, onDone }) {
  const [index, setIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  if (!world) return <FullBleedLoading />;
  const q = world.assessment[index];
  const chosen = answers[index];

  const pick = (i) => setAnswers([...answers.slice(0, index), i, ...answers.slice(index + 1)]);

  const next = async () => {
    if (index < world.assessment.length - 1) {
      setIndex(index + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      setLoading(true);
      try {
        const final = answers.slice(0, world.assessment.length);
        while (final.length < world.assessment.length) final.push(-1);
        const r = await api.submitAssessment(final, session);
        setSubmitted(r);
      } finally {
        setLoading(false);
      }
    }
  };

  if (submitted) {
    return (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="max-w-3xl mx-auto px-5 md:px-10 py-20" data-testid="xr-assessment-result">
        <div className="text-[11px] tracking-[0.24em] uppercase mythos-gold font-bold mb-3">Stage 07 · Discovery</div>
        <h2 className="font-mythos text-4xl md:text-5xl font-extrabold tracking-tight text-[#F4EFE6]">KNOWLEDGE DISCOVERY</h2>
        <div className="mt-8 vault-card rounded-2xl p-8 border border-[#C9A66B] text-center">
          <div className="mythos-gold font-mythos font-extrabold text-7xl">{submitted.score}<span className="text-3xl text-[#F4EFE6]/40"> / {submitted.total}</span></div>
          <div className="text-[11px] tracking-[0.22em] uppercase mythos-gold font-bold mt-2">concepts explored</div>
        </div>
        <div className="mt-8 space-y-3">
          {submitted.results.map((r, i) => (
            <div key={i} className={`vault-card rounded-xl p-4 border ${r.correct ? "border-[#C9A66B]/40" : "border-[#2A3349]"}`} data-testid={`xr-result-${i}`}>
              <div className="flex items-start gap-3">
                {r.correct ? <Check className="mythos-gold mt-0.5 flex-shrink-0" size={16} /> : <X className="text-[#7d8294] mt-0.5 flex-shrink-0" size={16} />}
                <div className="text-xs text-[#E8EBF0]/80 leading-relaxed">{r.explanation}</div>
              </div>
            </div>
          ))}
        </div>
        <button onClick={onDone} className="mt-10 mythos-gold-bg text-[#0E0F12] rounded-full px-7 py-3.5 font-bold inline-flex items-center gap-2 hover:opacity-90" data-testid="xr-assessment-done">
          Your journey <ArrowRight size={16} />
        </button>
      </motion.div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="max-w-3xl mx-auto px-5 md:px-10 py-16" data-testid="xr-assessment">
      <div className="text-[11px] tracking-[0.24em] uppercase mythos-gold font-bold mb-3">Stage 07 · Challenge · {index + 1}/{world.assessment.length}</div>
      <h3 className="font-mythos text-2xl md:text-3xl font-extrabold tracking-tight text-[#F4EFE6]">{q.prompt}</h3>
      <div className="mt-8 space-y-3">
        {q.options.map((o, i) => (
          <button
            key={i}
            onClick={() => pick(i)}
            data-testid={`xr-assessment-q${index}-opt${i}`}
            className={`w-full text-left vault-card rounded-xl p-4 border transition ${
              chosen === i ? "border-[#C9A66B]" : "border-[#2A3349] hover:border-[#C9A66B]/50"
            }`}
          >
            <div className="flex items-center gap-3">
              <div className={`w-7 h-7 rounded-full grid place-items-center text-[11px] font-bold ${chosen === i ? "mythos-gold-bg text-[#0E0F12]" : "border border-[#2A3349] text-[#F4EFE6]"}`}>
                {String.fromCharCode(65 + i)}
              </div>
              <div className="text-sm text-[#F4EFE6]/90">{o}</div>
            </div>
          </button>
        ))}
      </div>
      <button
        onClick={next}
        disabled={chosen === undefined || loading}
        className="mt-10 mythos-gold-bg text-[#0E0F12] rounded-full px-6 py-3 font-bold inline-flex items-center gap-2 hover:opacity-90 disabled:opacity-50"
        data-testid="xr-assessment-next"
      >
        {loading ? <Loader2 className="animate-spin" size={16} /> : index === world.assessment.length - 1 ? "See discovery" : "Next"} <ArrowRight size={16} />
      </button>
    </motion.div>
  );
}

// =========================== JOURNEY ===========================
function Journey({ submitted, onNext }) {
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="max-w-4xl mx-auto px-5 md:px-10 py-20" data-testid="xr-journey">
      <div className="text-[11px] tracking-[0.24em] uppercase mythos-gold font-bold mb-3">Stage 08 · Journey</div>
      <h2 className="font-mythos text-4xl md:text-5xl font-extrabold tracking-tight text-[#F4EFE6]">YOUR EDUSPHERE JOURNEY</h2>
      <div className="mt-10 grid sm:grid-cols-2 gap-4">
        {[
          { k: "Experience completed", v: "Kurukshetra" },
          { k: "Topics explored", v: "Indian epic tradition · Strategy · Geography · Characters · Historical interpretation" },
          { k: "Knowledge discovery", v: submitted ? `${submitted.score}/${submitted.total}` : "—" },
          { k: "Next recommended world", v: "Nalanda" },
        ].map((s, i) => (
          <div key={i} className="vault-card rounded-xl p-5 border border-[#2A3349]">
            <div className="text-[11px] tracking-[0.18em] uppercase mythos-gold font-bold">{s.k}</div>
            <div className="font-mythos font-bold text-[#F4EFE6] mt-1 text-sm">{s.v}</div>
          </div>
        ))}
      </div>
      <button onClick={onNext} className="mt-12 mythos-gold-bg text-[#0E0F12] rounded-full px-8 py-4 font-bold inline-flex items-center gap-2 hover:opacity-90 text-base" data-testid="xr-journey-next">
        EXPLORE MORE WORLDS <ArrowRight size={16} />
      </button>
    </motion.div>
  );
}

// =========================== WORLDS SHOWCASE ===========================
function WorldsShowcase() {
  const [worlds, setWorlds] = useState([]);
  useEffect(() => { api.worlds().then((d) => setWorlds(d.worlds)); }, []);
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="max-w-7xl mx-auto px-5 md:px-10 py-20" data-testid="xr-worlds-showcase">
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6 }}>
        <div className="text-[11px] tracking-[0.28em] uppercase mythos-gold font-bold mb-3">Stage 09 · Vision</div>
        <h2 className="font-mythos text-5xl md:text-7xl font-extrabold tracking-tight leading-[0.95] text-[#F4EFE6]">
          THIS IS ONLY <span className="mythos-gold">ONE WORLD.</span>
        </h2>
        <p className="text-[#E8EBF0]/70 mt-5 max-w-2xl text-lg">EDUSPHERE WORLDS — a growing collection of interactive learning experiences across India's history, culture, science and knowledge.</p>
      </motion.div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-12">
        {worlds.map((w, i) => {
          const Icon = WORLD_ICON[w.icon] || Sparkles;
          const avail = w.status === "available";
          return (
            <motion.div
              key={w.slug}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.06 }}
              data-testid={`xr-world-${w.slug}`}
              className={`vault-card rounded-2xl p-5 border transition ${avail ? "border-[#C9A66B]" : "border-[#2A3349] opacity-70"}`}
            >
              <div className="flex items-center justify-between mb-4">
                <Icon className={avail ? "mythos-gold" : "text-[#7d8294]"} size={22} />
                <span className={`text-[9px] tracking-[0.18em] uppercase font-bold ${avail ? "mythos-gold" : "text-[#7d8294]"}`}>
                  {avail ? "Unlocked" : "Coming soon"}
                </span>
              </div>
              <div className="font-mythos font-extrabold text-[#F4EFE6]">{w.title}</div>
              <div className="text-xs mythos-gold font-bold mt-1">{w.tagline}</div>
              <div className="text-[11px] text-[#E8EBF0]/55 mt-2">{w.era}</div>
            </motion.div>
          );
        })}
      </div>

      <div className="mt-16 flex flex-wrap gap-3">
        <Link to="/xr/partners" className="mythos-gold-bg text-[#0E0F12] rounded-full px-7 py-3.5 font-bold inline-flex items-center gap-2 hover:opacity-90" data-testid="xr-worlds-partner-cta" onClick={() => api.event("partner_interest_clicked", { from: "worlds_showcase" }, xrSession())}>
          Partner with us <ArrowRight size={16} />
        </Link>
        <Link to="/bharat-quest" className="border border-[#C9A66B] text-[#C9A66B] rounded-full px-7 py-3.5 font-bold inline-flex items-center gap-2 hover:bg-[#C9A66B] hover:text-[#0E0F12] transition">
          Try Bharat Quest <ArrowRight size={16} />
        </Link>
        <Link to="/" className="border border-[#2A3349] text-[#F4EFE6]/70 rounded-full px-7 py-3.5 font-bold hover:text-[#F4EFE6] hover:border-[#F4EFE6]/40 transition">← EduSphere home</Link>
      </div>
    </motion.div>
  );
}

function FullBleedLoading() {
  return (
    <div className="w-full h-[70vh] grid place-items-center mythos-ink-bg" data-testid="xr-loading">
      <Loader2 className="animate-spin mythos-gold" size={28} />
    </div>
  );
}
